import React, { useState, useContext } from "react";
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Autocomplete,
  Fab,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useMemo } from "react"; // Add this at top
import searchIcon from "../../assets/img/icons/search.png"; // update path if needed

import axios from "axios";
import { OrderContext } from "../../contexts/OrderContext";

const CreateItemType = () => {
  const { storeData, fetchStoreData } = useContext(OrderContext);
  const { itemtypes = [], metaltypes = [] } = storeData;

  const [showForm, setShowForm] = useState(false);
  const [itemType, setItemType] = useState({ name: "", metaltype_id: null });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMetalType, setFilterMetalType] = useState(null);

  const filteredItems = useMemo(() => {
    return itemtypes.filter((item) => {
      const matchesSearch = item.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesMetal = filterMetalType
        ? item.metaltype_id === filterMetalType.id
        : true;
      return matchesSearch && matchesMetal;
    });
  }, [searchTerm, itemtypes, filterMetalType]);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const mfr_id = localStorage.getItem("id");

  const handleItemNameChange = (e) => {
    setItemType({ ...itemType, name: e.target.value });
  };

  const handleMetalTypeChange = (_, newValue) => {
    setItemType({ ...itemType, metaltype_id: newValue ? newValue.id : null });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const requestData = {
        name: itemType.name,
        metaltype_id: itemType.metaltype_id,
        mfr_id,
      };

      await axios.post(
        `${API_BASE_URL}/api/fetchorder/CreateItemType`,
        requestData
      );
      fetchStoreData();
      setSnackbar({
        open: true,
        message: "Item type created successfully!",
        severity: "success",
      });
      setItemType({ name: "", metaltype_id: null });
      setShowForm(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error creating item type",
        severity: "error",
      });
    }

    setLoading(false);
  };

  return (
    <Box p={3} position="relative">
      {/* Existing Item Types */}
      {!showForm && (
        <Box maxWidth={600} mx="auto">
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap="0.4rem"
            sx={{
              // border: `0.5px solid #0D7377`,
              // borderRadius: 2,
              p: 1,
              mb: 3,
            }}
          >
            {/* Select metal */}
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                options={metaltypes}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Metal Type"
                    variant="outlined"
                    sx={{
                      "& label": { color: "#64748B" },
                      "& label.Mui-focused": { color: "#0D7377" },
                      "& .MuiInputBase-input": { color: "#333333" },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": { borderColor: "#A0ABBB" },
                        "&:hover fieldset": { borderColor: "#A0ABBB" },
                        "&.Mui-focused fieldset": { borderColor: "#0D7377" },
                      },
                    }}
                  />
                )}
                value={filterMetalType}
                onChange={(_, newValue) => setFilterMetalType(newValue)}
              />
            </Grid>

            {/* Search Bar */}
            <div className="custom-search-box" style={{ width: "100%" }}>
              <img src={searchIcon} alt="Search" className="search-icon" />
              <input
                type="text"
                placeholder="Search Item"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Create new order */}
            {filteredItems.length === 0 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                alignSelf="center"
                sx={{
                  width: "75%",
                  bgcolor: "#0D7377",
                  padding: "0.4rem 0",
                  borderRadius: 2,
                  cursor: "pointer",
                }}
                onClick={() => {
                  setItemType({
                    name: searchTerm,
                    metaltype_id: filterMetalType?.id || null,
                  });
                  setShowForm(true);
                }}
              >
                <Typography sx={{ fontSize: 16, color: "#fffffa" }}>
                  Create This Item
                </Typography>
              </Box>
            )}
          </Box>

          {/* Styled List */}
          <Grid container spacing={2} mb={10}>
            {filteredItems.map((item) => {
              const metal = metaltypes.find((m) => m.id === item.metaltype_id);
              return (
                <Grid item xs={12} key={item.id}>
                  <Card sx={{ p: 1, boxShadow: 1 }}>
                    <h5 style={{ margin: 0, fontSize: "14px" }}>{item.name}</h5>
                    <p
                      style={{
                        margin: "1px 0",
                        color: "#666",
                        fontSize: "12px",
                      }}
                    >
                      {metal?.name || " "}
                    </p>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {filteredItems?.length === 0 && (
            <Typography textAlign="center" color="gray" fontStyle="italic">
              No such item was found...
            </Typography>
          )}
        </Box>
      )}

      {/* Create Item Type Form */}
      {showForm && (
        <Card>
          <CardContent>
            <Typography
              variant="h5"
              fontWeight="bold"
              textAlign="center"
              mb={2}
              color="#0D7377"
            >
              Create Item Type
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Item Type Name"
                    value={itemType.name}
                    onChange={handleItemNameChange}
                    required
                    sx={{
                      "& label": {
                        color: "#64748B", // Label color
                      },
                      "& label.Mui-focused": {
                        color: "#0D7377", // when focused
                      },
                      "& .MuiInputBase-input": {
                        color: "#333333", // Input text color
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#A0ABBB", // Default border
                        },
                        "&:hover fieldset": {
                          borderColor: "#A0ABBB", // Hover border
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#0D7377", // Focus border
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Autocomplete
                    fullWidth
                    options={metaltypes}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Metal Type"
                        variant="outlined"
                        required
                        sx={{
                          "& label": {
                            color: "#64748B", // Label color
                          },
                          "& label.Mui-focused": {
                            color: "#0D7377", // when focused
                          },
                          "& .MuiInputBase-input": {
                            color: "#333333", // Input text color
                          },
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#A0ABBB", // Default border
                            },
                            "&:hover fieldset": {
                              borderColor: "#A0ABBB", // Hover border
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#0D7377", // Focus border
                            },
                          },
                        }}
                      />
                    )}
                    value={
                      metaltypes.find(
                        (metal) => metal.id === itemType.metaltype_id
                      ) || null
                    }
                    onChange={handleMetalTypeChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: "#0D7377",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#0A5C60", // Slightly darker on hover
                      },
                    }}
                    disabled={loading || !itemType.metaltype_id}
                  >
                    {loading ? "Saving..." : "Save"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Floating "+" Button */}
      {showForm && (
        <Fab
          color="primary"
          aria-label="toggle"
          onClick={() => setShowForm(!showForm)}
          sx={{
            position: "fixed",
            bottom: 80,
            right: 24,
            bgcolor: "#0D7377",
            "&:hover": { bgcolor: "#0A5C60" },
          }}
        >
          <CloseIcon />
        </Fab>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          '&.MuiSnackbar-root': {
            bottom: '100px !important', // force override snackbar position
          }
        }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateItemType;
